import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import { ProTable } from '@ant-design/pro-components';
import { App, Button, Flex, Space, Tag } from 'antd';
import PropTypes from 'prop-types';
import { useMemo, useRef, useState } from 'react';
import { queryOperationStores, updateOperationStoreAliases } from '../services/operations';
import DataLogDetailDrawer from './DataLogDetailDrawer';
import StoreAliasHealthAlerts from './StoreAliasHealthAlerts';
import StoreAliasesForm from './StoreAliasesForm';
import StoreNameIssuesDrawer from './StoreNameIssuesDrawer';

const StoreTable = ({ organizationId }) => {
  const actionRef = useRef(null);
  const [aliasHealth, setAliasHealth] = useState({
    duplicateNames: [],
    unmatchedNames: [],
  });
  const [businessStatusOptions, setBusinessStatusOptions] = useState([]);
  const [businessFormatOptions, setBusinessFormatOptions] = useState([]);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [nameIssuesOpen, setNameIssuesOpen] = useState(false);
  const { message } = App.useApp();
  const columns = useMemo(
    () => [
      { title: '门店编码', dataIndex: 'code' },
      { title: '门店名称', dataIndex: 'name' },
      { title: '门店别名', dataIndex: 'alias', hideInTable: true },
      {
        title: '门店别名',
        dataIndex: 'aliases',
        search: false,
        render: (_, record) =>
          record.aliases?.length ? (
            <Space wrap size={[4, 4]}>
              {record.aliases.map((alias) => (
                <Tag key={alias}>{alias}</Tag>
              ))}
            </Space>
          ) : (
            '-'
          ),
      },
      {
        title: '营业状态',
        dataIndex: 'businessStatus',
        valueType: 'select',
        fieldProps: {
          allowClear: true,
          options: businessStatusOptions,
        },
      },
      {
        title: '业态',
        dataIndex: 'businessFormat',
        valueType: 'select',
        fieldProps: {
          allowClear: true,
          options: businessFormatOptions,
        },
      },
      {
        title: '店长',
        dataIndex: 'managerName',
        render: (_, record) => record.managerNames?.join('、') || '-',
      },
      { title: '库管', dataIndex: 'inventoryManagerName', search: false },
      { title: '开业日期', dataIndex: 'openedAt', valueType: 'date', search: false },
      { title: '面积', dataIndex: 'area', valueType: 'digit', search: false },
      { title: '地域', dataIndex: 'region', search: false },
      { title: '营业地址', dataIndex: 'businessAddress', search: false },
      { title: '更新时间', dataIndex: 'updatedAt', valueType: 'dateTime', search: false },
      {
        title: '操作',
        valueType: 'option',
        render: (_, record) => [
          <Button
            key="detail"
            type="link"
            icon={<EyeOutlined />}
            onClick={() => setCurrentRecord(record)}
          >
            查看详情
          </Button>,
          <StoreAliasesForm
            candidateOptions={aliasHealth.unmatchedNames}
            key="aliases"
            record={record}
            trigger={
              <Button type="link" icon={<EditOutlined />}>
                维护别名
              </Button>
            }
            onFinish={async ({ aliases }) => {
              try {
                await updateOperationStoreAliases({
                  data: {
                    aliases: aliases || [],
                    id: record.id,
                    organizationId,
                  },
                });
                message.success('门店别名已更新');
                actionRef.current?.reload?.();
                return true;
              } catch (error) {
                message.error(error.message);
                return false;
              }
            }}
          />,
        ],
      },
    ],
    [
      aliasHealth.unmatchedNames,
      businessFormatOptions,
      businessStatusOptions,
      message,
      organizationId,
    ],
  );

  return (
    <Flex vertical gap="large">
      <StoreAliasHealthAlerts aliasHealth={aliasHealth} onManage={() => setNameIssuesOpen(true)} />
      <ProTable
        actionRef={actionRef}
        rowKey="id"
        headerTitle="门店数据"
        columns={columns}
        request={async (params) => {
          const result = await queryOperationStores({ ...params, organizationId });
          setAliasHealth(result.aliasHealth);
          setBusinessFormatOptions(result.meta.businessFormatOptions || []);
          setBusinessStatusOptions(result.meta.businessStatusOptions || []);
          return result;
        }}
        search={{ labelWidth: 88 }}
        scroll={{ x: 'max-content' }}
      />
      <DataLogDetailDrawer
        logType="stores"
        open={Boolean(currentRecord)}
        organizationId={organizationId}
        record={currentRecord}
        onClose={() => setCurrentRecord(null)}
      />
      <StoreNameIssuesDrawer
        open={nameIssuesOpen}
        organizationId={organizationId}
        onChanged={() => actionRef.current?.reload?.()}
        onClose={() => setNameIssuesOpen(false)}
      />
    </Flex>
  );
};

StoreTable.propTypes = {
  organizationId: PropTypes.string.isRequired,
};

export default StoreTable;
